import React from "react";
import { useAssessment, PHASES } from "../context/AssessmentContext";
import { isAbsenteeHighRisk } from "../utils/scoring";
import PageWrapper from "../components/layout/PageWrapper";
import ScoreGauge from "../components/ui/ScoreGauge";
import TierBadge from "../components/ui/TierBadge";
import TierGrid from "../components/results/TierGrid";
import DomainBreakdown from "../components/results/DomainBreakdown";
import AbsenteeRiskAlert from "../components/results/AbsenteeRiskAlert";
import LeadCaptureForm from "../components/results/LeadCaptureForm";
import ROIProjection from "../components/results/ROIProjection";
import BenchmarkCard from "../components/results/BenchmarkCard";
import ScoreShareCard from "../components/results/ScoreShareCard";
import Footer from "../components/layout/Footer";
import { useToast } from "../components/ui/Toast";

export default function ResultsPage() {
  const {
    scoreResult,
    lead,
    initSimulator,
    setPhase,
    reset,
  } = useAssessment();

  const toast = useToast();

  if (!scoreResult) {
    return (
      <PageWrapper>
        <p style={{ color: "var(--text-muted)", textAlign: "center", marginTop: 80 }}>
          No results yet. <button onClick={() => setPhase(PHASES.LANDING)} className="btn btn-ghost" style={{ padding: "6px 14px", fontSize: 13 }}>Start Assessment</button>
        </p>
      </PageWrapper>
    );
  }

  const { score, tier, domainScores, penalties } = scoreResult;
  const absenteeRisk = isAbsenteeHighRisk(domainScores);

  return (
    <>
    <PageWrapper maxWidth={860}>
      {/* ── Page header ── */}
      <div className="fade-up" style={{ textAlign: "center", marginBottom: 40 }}>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--text-muted)",
          letterSpacing: "0.15em",
          marginBottom: 10,
        }}>
          BALLOT D.A. · ELECTION READINESS SCORER™
        </div>
        <h1 style={{
          fontFamily: "var(--font-display)",
          fontSize: "clamp(24px, 3.5vw, 36px)",
          fontWeight: 700,
          color: "var(--text-primary)",
          letterSpacing: "-0.02em",
        }}>
          Your Results Report
        </h1>
      </div>

      {/* ── Score hero card ── */}
      <div className="fade-up-1" style={{
        padding: "44px 32px",
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-xl)",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
        marginBottom: 24,
      }}>


        <ScoreGauge score={score} size={220} animate />

        <div style={{ marginTop: 24, marginBottom: 16 }}>
          <TierBadge tier={tier} size="xl" />
        </div>

        <p style={{
          fontSize: 15,
          color: "var(--text-secondary)",
          maxWidth: 440,
          margin: "0 auto 10px",
          lineHeight: 1.6,
        }}>
          {tier.description}
        </p>

        <p style={{
          fontSize: 13,
          color: tier.color,
          fontStyle: "italic",
          opacity: 0.85,
        }}>
          {tier.urgency}
        </p>

        {penalties < 0 && (
          <div style={{
            marginTop: 14,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 12px",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)",
            borderRadius: 99,
            fontSize: 12,
            color: "#f87171",
            fontFamily: "var(--font-mono)",
          }}>
            ⚠️ Penalty modifiers applied: {penalties} pts
          </div>
        )}
      </div>

      {/* ── Tier grid ── */}
      <div className="fade-up-2" style={{ marginBottom: 24 }}>
        <TierGrid currentTier={tier} />
      </div>

      {/* ── Absentee risk alert (conditional) ── */}
      {absenteeRisk && (
        <AbsenteeRiskAlert
          onDemoClick={() => toast.info("Absentee Demo request received — a consultant will contact you shortly")}
          onAuditClick={() => toast.success("Audit request submitted — we'll be in touch within 24 hours")}
        />
      )}

      {/* ── Domain breakdown ── */}
      <div className="fade-up-2" style={{ marginBottom: 28 }}>
        <DomainBreakdown domainScores={domainScores} />
      </div>

      {/* ── ROI Projection ── */}
      <div className="fade-up-3" style={{ marginBottom: 28 }}>
        <ROIProjection domainScores={domainScores} />
      </div>

      {/* ── Benchmark comparison ── */}
      <div className="fade-up-3" style={{ marginBottom: 28 }}>
        <BenchmarkCard score={score} />
      </div>

      {/* ── Share card ── */}
      <div className="fade-up-3" style={{ marginBottom: 28 }}>
        <div style={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: 18,
          color: "var(--text-primary)",
          marginBottom: 12,
        }}>
          Share Your Score
        </div>
        <ScoreShareCard score={score} domainScores={domainScores} lead={lead} />
      </div>

      {/* ── Lead capture ── */}
      <div className="fade-up-3" style={{ marginBottom: 28 }}>
        <LeadCaptureForm />
      </div>

      {/* ── Primary CTAs ── */}
      <div className="fade-up-3" style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        marginBottom: 28,
      }}>
        <button
          onClick={initSimulator}
          className="btn"
          style={{
            background: "var(--accent-blue)",
            color: "#fff",
            padding: "18px",
            fontSize: 15,
            flexDirection: "column",
            gap: 4,
            height: "auto",
          }}
        >
          <span style={{ fontSize: 22 }}>🧪</span>
          <span>Simulate a Better Setup</span>
          <span style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            opacity: 0.7,
            fontWeight: 400,
          }}>
            See your potential score →
          </span>
        </button>

        <button
          className="btn"
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            color: "var(--text-primary)",
            padding: "18px",
            fontSize: 15,
            flexDirection: "column",
            gap: 4,
            height: "auto",
          }}
          onClick={() => toast.success("Consultation request received — we'll send a calendar invite within 24 hours")}
        >
          <span style={{ fontSize: 22 }}>📅</span>
          <span>Book a Consultation</span>
          <span style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--text-muted)",
            fontWeight: 400,
          }}>
            Free 30-min strategy call →
          </span>
        </button>
      </div>

      {/* ── Secondary CTAs ── */}
      <div className="fade-up-4" style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 10,
        marginBottom: 32,
      }}>
        {[
          { icon: "📥", label: "Download Report", sublabel: "PDF with recommendations", msg: "success" },
          { icon: "📬", label: "Absentee Audit",  sublabel: "Free process review",       msg: "info"    },
          { icon: "🔗", label: "See Products",    sublabel: "Ballot D.A. solutions",     msg: "info"    },
        ].map((cta) => (
          <button
            key={cta.label}
            className="btn btn-ghost"
            style={{
              flexDirection: "column",
              gap: 4,
              padding: "14px",
              height: "auto",
              fontSize: 13,
            }}
            onClick={() => toast[cta.msg](`${cta.label} — connect your integration here`)}
          >
            <span style={{ fontSize: 18 }}>{cta.icon}</span>
            <span>{cta.label}</span>
            <span style={{
              fontSize: 10,
              color: "var(--text-muted)",
              fontWeight: 400,
            }}>
              {cta.sublabel}
            </span>
          </button>
        ))}
      </div>

      {/* ── Retake ── */}
      <div style={{ textAlign: "center" }}>
        <button
          onClick={reset}
          style={{
            background: "none",
            border: "none",
            color: "var(--text-muted)",
            cursor: "pointer",
            fontSize: 13,
            textDecoration: "underline",
          }}
        >
          ↩ Retake Assessment
        </button>
      </div>
    </PageWrapper>
    <Footer />
    </>
  );
}
